import { useState, useEffect } from 'react';
import { useParams } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { useUnitStore } from '../../hooks/useUnits';
import { Button, Card, CardContent, useToast } from '@lancar-pos/ui';

export function UnitFormPage() {
  const { unitId } = useParams({ from: '/app/inventory/units/$unitId' });
  const { units, addUnit, updateUnit } = useUnitStore();
  const toast = useToast();
  
  const isNew = unitId === 'new';
  const existingUnit = !isNew ? units.find((u) => u.id === unitId) : undefined;
  
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [type, setType] = useState<'count' | 'weight' | 'length' | 'volume'>('count');
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (existingUnit) {
      setName(existingUnit.name);
      setAbbreviation(existingUnit.abbreviation);
      setType(existingUnit.type);
      setIsDefault(existingUnit.isDefault);
    }
  }, [existingUnit]);

  const handleSave = async () => {
    if (!name.trim() || !abbreviation.trim()) {
      toast.error('Nama dan singkatan wajib diisi');
      return;
    }

    setIsLoading(true);
    try {
      if (isNew) {
        await addUnit({
          name: name.trim(),
          abbreviation: abbreviation.trim(),
          type,
          isDefault,
        });
        toast.success('Satuan ditambahkan');
      } else {
        await updateUnit(unitId, {
          name: name.trim(),
          abbreviation: abbreviation.trim(),
          type,
          isDefault,
        });
        toast.success('Satuan diperbarui');
      }
      window.history.back();
    } catch (error) {
      toast.error(isNew ? 'Gagal menambahkan satuan' : 'Gagal memperbarui satuan');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? 'Tambah Satuan' : 'Edit Satuan'}
            </h1>
          </div>
          <Button onClick={handleSave} loading={isLoading}>
            <Save className="w-4 h-4 mr-2" />
            Simpan
          </Button>
        </div>
      </header>

      <main className="max-w-xl mx-auto p-4 lg:p-6">
        <Card>
          <CardContent className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Satuan *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Pieces"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Singkat *
              </label>
              <input
                type="text"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
                placeholder="Contoh: pcs"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipe
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="count">Jumlah</option>
                <option value="weight">Berat</option>
                <option value="length">Panjang</option>
                <option value="volume">Volume</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isDefault"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <label htmlFor="isDefault" className="text-sm text-gray-700">
                Jadikan default
              </label>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
